<script setup lang="ts">
import type { CorporatePageApiModel, GrowSubHomeHeaderApiModel } from '@gro-up/api/corporate'
import { HighlightedTags } from '@gro-up/ui/components'
import { computed, useTemplateRef } from 'vue'

import { useDomain } from '../../composables/use-domain'
import { useHeroParallax } from '../../composables/useHeroParallax'
import CorporateGrowSubhomeHero from '../blocks/CorporateGrowSubhomeHero/CorporateGrowSubhomeHero.vue'
import PreFooter from '../blocks/PreFooter/PreFooter.vue'

import HeroArch from './HeroArch.vue'
import PageBlock from './PageBlock.vue'

import { useRuntimeConfig } from '#app'
import { useSeoMeta, useHead } from '#imports'

export type PageProps = {
  page: CorporatePageApiModel
}

const props = defineProps<PageProps>()

const domain = useDomain()
const config = useRuntimeConfig()

useHead({
  htmlAttrs: {
    lang: 'nl',
  },
  link: [
    {
      rel: 'canonical',
      href: props.page.canonicalUrl,
    },
  ],
  meta: [
    {
      name: 'robots',
      content:
        config.public.indexable === false
          ? 'noindex, nofollow'
          : `${props.page.noIndex ? 'no' : ''}index, ${props.page.noFollow ? 'no' : ''}follow`,
    },
  ],
  script: props.page.structuredData?.map(data => ({
    type: 'application/ld+json',
    textContent: data,
  })),
})

useSeoMeta({
  title: `${props.page.meta?.title ?? 'gro-up'}`,
  description: props.page.meta?.description,
  ogUrl: props.page.canonicalUrl,
  ogTitle: `${props.page.name ?? 'gro-up'}`,
  ogDescription: props.page.meta?.description,
  ogType: 'website',
  ogImage: props.page.og?.image?.src,
  ogImageAlt: props.page.og?.image?.altText,
  ogImageWidth: props.page.og?.image?.width,
  ogImageHeight: props.page.og?.image?.height,
  ogLocale: 'nl_NL',
  ogSiteName: props.page.siteName ?? 'gro-up',
  twitterCard: 'summary_large_image',
  twitterTitle: `${props.page.twitter?.title ?? 'gro-up'}`,
  twitterDescription: props.page.twitter?.description,
  twitterImage: props.page.twitter?.image?.src,
  twitterImageAlt: props.page.twitter?.image?.altText,
  themeColor: domain.value
    ? {
        'child-care': '#FBF5EA',
        'head-office': '#FAF8F5',
        'community-work': '#FFE5E5',
        'youth-support': '#E9F3F4',
        'maternity-care': '#F8EBD7',
      }[domain.value]
    : '#1F122E',
})

// The grow sub-home hero is rendered explicitly in the parallax hero layer; its
// highlighted tags are rendered in the content layer (on top of the arch), and
// every other block is rendered generically below.
const hero = computed<GrowSubHomeHeaderApiModel | undefined>(
  () => props.page.blocks?.find(block => block.type === 'growSubHomeHeader')?.value as GrowSubHomeHeaderApiModel | undefined,
)
const hasHero = computed(() => !!hero.value)
const tags = computed(() => hero.value?.highlightedTags?.entries ?? [])
const blocks = computed(() =>
  props.page.blocks?.filter(block => block.type !== 'growSubHomeHeader' && block.type !== 'stickyBar'),
)

// Editable per page (CMS), falls back to a site-wide default resolved by the backend. The backend always
// sends a `preFooter` object (even when the editor hasn't enabled it on this page), with an empty
// `articles` array as the "not available" signal — so gate on that rather than mere object presence.
const preFooter = computed(() => {
  const value = props.page.preFooter
  return value && value.articles.length > 0 ? value : undefined
})

// Parallax: the hero scrolls slower than the (opaque) content, so the content
// slides up and over it with the arch as the separation. See useHeroParallax.
const heroContainer = useTemplateRef<HTMLElement>('heroContainer')
useHeroParallax(heroContainer, { enabled: hasHero, speed: 0.4, startMargin: 80 })
</script>

<template>
  <div class="flex grow flex-col">
    <div v-if="hasHero" ref="heroContainer" class="hero-container relative z-0">
      <CorporateGrowSubhomeHero v-if="hero" v-bind="hero" />
    </div>

    <div class="components relative z-10" :class="{ 'pt-6 md:pt-32': hasHero }">
      <!--
        Arch: curved separation that the content slides over. Absolutely positioned
        so it doesn't take layout space; pulled slightly up into the hero (-translate-y)
        for the overlap. Centred and horizontally clipped so the fixed-width SVG keeps
        its size on smaller screens with its faded sides overflowing out of view.
        Its opaque body (full width below the curve) covers the hero during parallax,
        and its gradient fades into the page background, so the content needs no bg.
      -->
      <div
        v-if="hasHero"
        class="pointer-events-none absolute inset-x-0 top-0 z-0 flex -translate-y-12 justify-center overflow-x-clip before:absolute before:inset-y-full before:left-0 before:right-0 before:h-full before:bg-head-office-20 before:-translate-y-0.5">
        <HeroArch />
      </div>

      <div class="relative z-10 flex grow flex-col gap-16 lg:gap-32">
        <HighlightedTags v-if="tags.length" mode="link" :entries="tags" />

        <PageBlock
          v-for="(block, index) in blocks"
          :key="`page-block-${index}`"
          :block="block"
          :class="{ 'subhome-block--narrow': index >= 1 }" />
      </div>
    </div>

    <div class="relative z-10">
      <PreFooter v-if="preFooter" v-bind="preFooter" />
    </div>
  </div>
</template>

<style scoped>
/*
  Mobiel: alle blokken onder het eerste ("uitgelicht") blok krijgen een ruimere zijmarge zodat ze
  smaller in beeld staan. --content-gutter wordt overgeërfd door de blokken (Container, carousel- en
  slide-gutters), die er standaard op terugvallen via de clamp-fallback. Desktop blijft ongewijzigd.
*/
@media (max-width: 767px) {
  .subhome-block--narrow {
    --content-gutter: 3.75rem;
  }
}
</style>
